import React, { useMemo } from "react";
import { riskColor, riskLabel, riskEmoji, aggregateByDistrict } from "../utils/risk";

// Approximate SVG coordinates for Gujarat districts (within 800x550 viewBox)
const DISTRICT_POSITIONS = {
  Gandhinagar: { x: 430, y: 200, label: "Gandhinagar" },
  Anand:       { x: 420, y: 290, label: "Anand" },
  Tapi:        { x: 310, y: 390, label: "Tapi" },
  Kutchh:      { x: 160, y: 150, label: "Kutchh" },
  Rajkot:      { x: 295, y: 255, label: "Rajkot" },
};

export default function GeoMap({ predictions }) {
  const districtStats = useMemo(() =>
    aggregateByDistrict(predictions)
      .filter(d => DISTRICT_POSITIONS[d.name])
      .map(d => ({ ...d, pos: DISTRICT_POSITIONS[d.name] })),
  [predictions]);

  const allDistricts = Object.keys(DISTRICT_POSITIONS);
  const missing = allDistricts.filter(d => !districtStats.find(s => s.name === d));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={s.card}>
        <h3 style={s.title}>Geographic Risk Distribution — Gujarat POC Districts</h3>

        {/* SVG Map */}
        <div style={s.mapWrap}>
          <svg viewBox="0 0 800 550" style={s.svg}>

            {/* Gujarat state outline (simplified) */}
            <path
              d="M 100 80 L 200 60 L 300 70 L 380 90 L 470 80 L 540 110 L 560 160
                 L 520 200 L 500 250 L 480 320 L 460 380 L 430 430 L 390 460
                 L 340 470 L 280 450 L 240 420 L 200 400 L 160 380 L 120 340
                 L 90 280 L 80 200 L 100 80 Z"
              fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="2"
            />

            {/* Kutchh region (top-left, larger) */}
            <path
              d="M 100 80 L 200 60 L 240 90 L 260 130 L 220 160 L 180 170 L 130 160 L 90 140 L 100 80 Z"
              fill="#e2e8f0" stroke="#cbd5e1" strokeWidth="1"
            />

            {/* Gulf of Kutchh label */}
            <text x="95" y="260" fill="#94a3b8" fontSize="11" fontStyle="italic">Gulf of Kutchh</text>
            <text x="370" y="500" fill="#94a3b8" fontSize="11" fontStyle="italic">Gulf of Khambhat</text>
            <text x="550" y="180" fill="#94a3b8" fontSize="11" fontStyle="italic">Rajasthan</text>
            <text x="490" y="430" fill="#94a3b8" fontSize="11" fontStyle="italic">Maharashtra</text>

            {/* Compass */}
            <text x="680" y="80" fontSize="22" textAnchor="middle">🧭</text>
            <text x="680" y="100" fontSize="10" fill="#6b7280" textAnchor="middle">N</text>

            {/* District markers — missing (grey) */}
            {missing.map(name => {
              const pos = DISTRICT_POSITIONS[name];
              return (
                <g key={name}>
                  <circle cx={pos.x} cy={pos.y} r={22} fill="#e5e7eb" stroke="#9ca3af" strokeWidth="2" opacity={0.7} />
                  <text x={pos.x} y={pos.y + 1} textAnchor="middle" dominantBaseline="middle" fontSize="10" fill="#9ca3af" fontWeight="600">No data</text>
                  <text x={pos.x} y={pos.y + 34} textAnchor="middle" fontSize="11" fill="#6b7280" fontWeight="600">{name}</text>
                </g>
              );
            })}

            {/* District markers — with data */}
            {districtStats.map(d => {
              const r = Math.max(24, Math.min(44, 24 + d.count * 0.5));
              const color = riskColor(d.avgRisk);
              return (
                <g key={d.name}>
                  {/* Pulse ring */}
                  <circle cx={d.pos.x} cy={d.pos.y} r={r + 8} fill={color} opacity={0.15} />
                  {/* Main circle */}
                  <circle cx={d.pos.x} cy={d.pos.y} r={r} fill={color} opacity={0.85} stroke="#fff" strokeWidth="3" />
                  {/* Risk % text */}
                  <text x={d.pos.x} y={d.pos.y - 4} textAnchor="middle" dominantBaseline="middle" fontSize="12" fill="#fff" fontWeight="800">
                    {(d.avgRisk * 100).toFixed(0)}%
                  </text>
                  <text x={d.pos.x} y={d.pos.y + 10} textAnchor="middle" dominantBaseline="middle" fontSize="9" fill="rgba(255,255,255,0.9)">
                    {riskLabel(d.avgRisk)}
                  </text>
                  {/* District name label */}
                  <text x={d.pos.x} y={d.pos.y + r + 14} textAnchor="middle" fontSize="12" fill="#111827" fontWeight="700">{d.name}</text>
                  <text x={d.pos.x} y={d.pos.y + r + 27} textAnchor="middle" fontSize="10" fill="#6b7280">{d.count} beneficiaries</text>
                </g>
              );
            })}
          </svg>

          {/* Tooltip panel on right */}
          {districtStats.length > 0 && (
            <div style={s.tooltipPanel}>
              <div style={s.tooltipTitle}>District Details</div>
              {districtStats.map(d => (
                <div key={d.name} style={{ ...s.tooltipItem, borderLeft: `4px solid ${riskColor(d.avgRisk)}` }}>
                  <div style={s.tooltipDistrict}>{d.name}</div>
                  <div style={s.tooltipRow}><span>Malnutrition</span><span>{d.malnutrition}%</span></div>
                  <div style={s.tooltipRow}><span>Anemia</span><span>{d.anemia}%</span></div>
                  <div style={s.tooltipRow}><span>Growth</span><span>{d.growth}%</span></div>
                  <div style={{ ...s.tooltipRow, fontWeight: 700, color: riskColor(d.avgRisk) }}>
                    <span>Overall</span><span>{(d.avgRisk * 100).toFixed(1)}% — {riskLabel(d.avgRisk)}</span>
                  </div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{d.count} beneficiaries</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Legend */}
        <div style={s.legend}>
          {[
            { color: "#dc2626", label: "High Risk (≥ 80%)" },
            { color: "#d97706", label: "Medium Risk (50–79%)" },
            { color: "#057a55", label: "Low Risk (< 50%)" },
            { color: "#9ca3af", label: "No Data" },
          ].map(l => (
            <div key={l.label} style={s.legendItem}>
              <div style={{ width: 14, height: 14, borderRadius: "50%", background: l.color }} />
              <span style={{ fontSize: 12, color: "#374151" }}>{l.label}</span>
            </div>
          ))}
          <div style={s.legendItem}>
            <div style={{ width: 20, height: 12, borderRadius: 6, background: "linear-gradient(90deg,#057a55,#dc2626)" }} />
            <span style={{ fontSize: 12, color: "#374151" }}>Circle size = beneficiary count</span>
          </div>
        </div>
      </div>

      {/* Summary table */}
      {districtStats.length > 0 && (
        <div style={s.card}>
          <h3 style={s.title}>District Risk Summary</h3>
          <table style={s.table}>
            <thead>
              <tr>
                {["District", "Beneficiaries", "Malnutrition %", "Anemia %", "Growth %", "Avg Risk", "Status"].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {districtStats.map((d, i) => (
                <tr key={d.name} style={{ background: i % 2 === 0 ? "#f9fafb" : "#fff" }}>
                  <td style={s.td}><strong>{d.name}</strong></td>
                  <td style={s.td}>{d.count}</td>
                  <td style={s.td}>{d.malnutrition}%</td>
                  <td style={s.td}>{d.anemia}%</td>
                  <td style={s.td}>{d.growth}%</td>
                  <td style={s.td}>{(d.avgRisk * 100).toFixed(1)}%</td>
                  <td style={s.td}>
                    <span style={{ color: riskColor(d.avgRisk), fontWeight: 700 }}>
                      {riskEmoji(d.avgRisk)} {riskLabel(d.avgRisk)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {districtStats.length === 0 && (
        <div style={s.card}>
          <p style={{ color: "#9ca3af", textAlign: "center", padding: 20 }}>
            No district data found. Ensure uploaded data has a <code>district</code> column with values: Gandhinagar, Tapi, Anand, Kutchh, Rajkot.
          </p>
        </div>
      )}
    </div>
  );
}

const s = {
  card: { background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  title: { margin: "0 0 16px", fontSize: 16, fontWeight: 700, color: "#111827" },
  mapWrap: { display: "flex", gap: 20, alignItems: "flex-start" },
  svg: { flex: 1, border: "1px solid #e5e7eb", borderRadius: 10, background: "#f8fafc", minWidth: 0 },
  tooltipPanel: { width: 220, flexShrink: 0, display: "flex", flexDirection: "column", gap: 10 },
  tooltipTitle: { fontWeight: 700, fontSize: 13, color: "#111827", marginBottom: 4 },
  tooltipItem: { background: "#f9fafb", borderRadius: 8, padding: "10px 12px", borderLeft: "4px solid #1a56db" },
  tooltipDistrict: { fontWeight: 700, fontSize: 13, color: "#111827", marginBottom: 6 },
  tooltipRow: { display: "flex", justifyContent: "space-between", fontSize: 12, color: "#374151", marginBottom: 2 },
  legend: { display: "flex", gap: 16, flexWrap: "wrap", marginTop: 14, alignItems: "center" },
  legendItem: { display: "flex", alignItems: "center", gap: 6 },
  table: { borderCollapse: "collapse", width: "100%", fontSize: 13 },
  th: { background: "#1a56db", color: "#fff", padding: "10px 14px", textAlign: "left", fontWeight: 600, whiteSpace: "nowrap" },
  td: { padding: "8px 14px", borderBottom: "1px solid #f3f4f6", verticalAlign: "middle" },
};
